import { useContext, useEffect, useState } from "react";
import { Props } from "./AccordionDetailsProps";
import MainContext from "../../../context/mainContext";
import { ISummaryReviewModel, ISummaryReviewerModel } from "../../../models/summary.model";
import summaryService from "../../../services/summary.service";
import SpinnerArea from "../../../components/SpinnerArea";
import { Box, Link, Table, TableBody, TableCell, TableRow } from "@mui/material";
import styles from "./AccordionDetails.module.scss";
import Empty from "./Empty";
import { t } from "i18next";
import { OpenIcon } from "../../../assets/icons";
import DataTable from "../../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import DocumentDownload from "../../../components/DocumentDownload";
import IconTooltip from "../../../components/IconTooltip";

const SummaryReview: React.FC<Props> = ({ publicationId, expanded }) => {
  const { service } = useContext(MainContext);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reviewData, setReviewData] = useState<ISummaryReviewModel>();

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("reviewer")!,
      sortable: false,
      width: 200,
    },
    {
      field: "uploadDate",
      headerName: t("uploadDate")!,
      sortable: false,
      width: 120,
      renderCell: (params) => moment(params.row.uploadDate).format("MM/DD/YYYY"),
    },
    {
      field: "status",
      headerName: t("status")!,
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return (
          <>
            {params.row.status}{" "}
            {params.row.status === "Pending" && params.row.dueDate && (
              <IconTooltip title={t("deadlineTooltip", { dueDate: moment(params.row.dueDate).format("MM/DD/yyyy") })} />
            )}
          </>
        );
      },
    },
    {
      field: "comments",
      headerName: t("comments")!,
      sortable: false,
      minWidth: 200,
      flex: 1,
      cellClassName: styles.noEllipsis,
      renderCell: (params) => {
        return <p>{params.row.comments}</p>;
      },
    },
    {
      field: "documentId",
      headerName: t("document")!,
      headerClassName: "document",
      sortable: false,
      cellClassName: "document",
      width: 70,
      renderCell: (params) => {
        return params.row.documentId != null ? <DocumentDownload documentId={params.row.documentId} /> : <></>;
      },
    },
  ];

  useEffect(() => {
    let active = true;

    if (active && expanded && !loaded) {
      service(() => summaryService.review(publicationId), {
        loader: setLoading,
        callback: (data) => {
          setReviewData(data);
          setLoaded(true);
        },
      });
    }

    return () => {
      active = false;
    };
  }, [expanded, loaded, publicationId, service]);

  return (
    <SpinnerArea loading={loading}>
      {reviewData ? (
        <Box className={styles.review}>
          {reviewData.dontNeedReview ? (
            <Empty messge={t("dontNeedReview")} />
          ) : (
            <>
              <Table className={styles.draftHeader}>
                <TableBody>
                  <TableRow>
                    <TableCell className={styles.viewCommentsOnline}>
                      <Link href={reviewData.draft.link} variant="button" target="_blank" rel="noopener noreferrer">
                        <OpenIcon />
                        <p>{t("viewCommentsOnline")}</p>
                      </Link>
                    </TableCell>
                    <TableCell className={styles.download}>
                      <DocumentDownload documentId={reviewData?.draft.id!} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <DataTable<ISummaryReviewerModel>
                rows={reviewData.reviewers}
                columns={columns}
                emptyMessage={t("noRevierwersAssigned")}
                loading={false}
                autoHeight
                rowAutoHeight
              />
            </>
          )}
        </Box>
      ) : (
        <Empty messge={t("noRevierwersAssigned")} />
      )}
    </SpinnerArea>
  );
};

export default SummaryReview;
